import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex scale-100 transition-all focus-visible:!border-primary duration-100 active:scale-[0.98] items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        unstyled: '',
        default:
          'bg-orange-500 text-white hover:bg-orange-600 focus-visible:ring-orange-500/20 disabled:bg-white disabled:text-neutral-500 disabled:border disabled:border-neutral-300',
        secondary:
          'bg-neutral-950 text-white hover:bg-neutral-800 focus-visible:ring-neutral-950/20 disabled:bg-white disabled:text-neutral-500 disabled:border disabled:border-neutral-300',
        tertiary:
          'bg-white text-neutral-950 border border-neutral-300 hover:bg-neutral-100 focus-visible:ring-neutral-300/20 disabled:bg-white disabled:text-neutral-500 disabled:border-neutral-300',
        dark: 'bg-neutral-950 text-white hover:bg-neutral-800',
        destructive: 'bg-destructive text-white border border-destructive hover:bg-red-700 disabled:bg-white disabled:text-neutral-500 disabled:border-neutral-300',
        outline: 'border border-neutral-950 bg-transparent text-neutral-950 hover:bg-neutral-100',
        ghost: 'border border-transparent hover:bg-neutral-200-60',
      },
      // Heights are set as "min" heights, because sometimes Ai will place large amount of content
      // inside buttons. With a min-height they will look appropriate with small amounts of content,
      // but will expand to fit large amounts of content.
      size: {
        default: 'min-h-9 px-4 py-2 text-sm [&_svg]:size-4',
        sm: 'min-h-8 px-3 text-xs [&_svg]:size-3.5',
        lg: 'min-h-10 px-6 text-base [&_svg]:size-5',
        icon: 'size-8 [&_svg]:!size-4',
        'icon-sm': 'size-6 [&_svg]:!size-3.5 rounded-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

type ButtonVariantProps = VariantProps<typeof buttonVariants>

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonVariantProps>,
    ButtonVariantProps {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...restProps }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          variant === 'unstyled' ? className : buttonVariants({ variant, size, className }),
        )}
        ref={ref}
        {...restProps}
        {...(!asChild
          ? {
              // Prevent accidental form submissions when `Button` is used inside a `<form>`
              // without an explicit `type` (HTML default is `submit`).
              type: type ?? 'button',
            }
          : type
            ? { type }
            : {})}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
